import {
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { environment } from '../environments/environment';
import { AccountService } from './shared/account/account.service';
import { IdTokenClaimsWithPolicyId } from './shared/auth/id-token-claims-with-policy-id.type';
import {
  filter,
  takeUntil,
} from 'rxjs/operators';
import { b2cPolicies } from './auth.config';
import {
  AccountInfo,
  StringDict,
} from '@azure/msal-common';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { MsalBroadcastService } from '@azure/msal-angular';
import { Subject } from 'rxjs';
import { LocalStorageService } from './shared/local-storage/local-storage.service';
import { Router } from '@angular/router';
import { constants } from './shared/constants';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'spa-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  signedInDisplay: boolean = false;
  showModToggle: boolean = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private accountService: AccountService,
    private msalBroadcastService: MsalBroadcastService,
    private localStorageService: LocalStorageService,
    private router: Router) { }

  ngOnInit() {
    this.setSignedInDisplay();

    this.subscribeToMsalAccountAdded();
    this.subscribeToMsalAccountRemoved();

    this.subscribeToMsalInProgressEvents();
    this.subscribeToMsalSuccessEvents();
    this.subscribeToMsalFailureEvents();

    window.addEventListener('storage', (event: StorageEvent) => this.storageListener(event));    
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    window.removeEventListener('storage', this.storageListener);
  }

  async login(journey?: string) {
    const authFlowRequest: RedirectRequest = {
      scopes: [environment.scope],
    };

    if (journey) {
      const queryParams: StringDict = {
        ['journey']: journey,
      };

      authFlowRequest.extraQueryParameters = queryParams;
    }

    this.accountService.login(authFlowRequest);
  }

  platform_identity_logout() {
    this.accountService.logout();
  }
 
  setSignedInDisplay(): void {
    this.signedInDisplay = this.accountService.accountExists();
  }

  private subscribeToMsalInProgressEvents() {
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md#the-inprogress-observable
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setSignedInDisplay();
        this.accountService.checkAndSetActiveAccount();
      });
  }

  private subscribeToMsalAccountAdded(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        console.log('user logged in');
        location.reload();
      });
  }

  private subscribeToMsalAccountRemoved(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_REMOVED),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        console.log('user logged out');
        location.reload();
      });
  }

  private subscribeToMsalSuccessEvents(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
          || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          || msg.eventType === EventType.SSO_SILENT_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {

        const payload = result.payload as AuthenticationResult;
        if (payload.state) {
          this.localStorageService.setAdditionalState(payload.state);
        }

        const idTokenClaims = payload.idTokenClaims as IdTokenClaimsWithPolicyId;
        const acrLower = idTokenClaims.acr?.toLowerCase();
        const tfpLower = idTokenClaims.tfp?.toLowerCase();

        this.updateActiveAccountIfUserAuthenticated(acrLower, tfpLower, payload.account);
      });
  }

  private updateActiveAccountIfUserAuthenticated(acrLower: string, tfpLower: string, account: AccountInfo) {
    const signUpSignInPolicyLower = b2cPolicies.names.signUpSignIn.toLowerCase();
    if (acrLower === signUpSignInPolicyLower || tfpLower === signUpSignInPolicyLower) {
      this.accountService.setActiveAccount(account);
    }
  }

  private subscribeToMsalFailureEvents(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE
          || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const noSessionPromptNoneError = 'AADB2C90077';
        if ((result.eventType === EventType.ACQUIRE_TOKEN_FAILURE)
          && (result.error?.message?.includes(noSessionPromptNoneError))) {

          /*
           * MSAL recommends that in this scenario, where acquire tokens has failed because
           * the user had no valid tokens, the refresh token was expired and the user had
           * no valid SSO session, the application should fall back on either
           * acquireTokenPopup or acquireTokenRedirect
           */
          this.accountService.acquireTokenRedirect();
          return;
        }

        this.router.navigate(['/home', { errorMessage: result.error.message }]);
      });
  }

  private storageListener(e: StorageEvent): void {
    if (e.key === constants.migrationOnDemandEnabledKeyInStorage) {
      console.log(`User has tampered with ${constants.migrationOnDemandEnabledKeyInStorage} - logging user out`);
      this.platform_identity_logout();
    }
  }
}
